import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useI18n } from '../../contexts/I18nContext';
import api from '../../services/api';
import { localizePath, stripLocalePrefix } from '../../utils/localePaths';
import { useScopedUiI18n } from '../../i18n/scopedUiTranslations';

const NAV_KEYS = [
  { labelKey: 'fortune', items: [
    { to: '/fortune', sym: '占', nameKey: 'aiFortune' },
    { to: '/saju-garden', sym: '林', nameKey: 'sajuGarden' },
    { to: '/character-chat/hammyo', sym: '暖', nameKey: 'hammyoSaju' },
    { to: '/character-chat/keonyang', sym: '明', nameKey: 'keonyangSaju' },
    { to: '/character-chat/sanharabeoji', sym: '山', nameKey: 'sanharabeojiSaju' },
    { to: '/character-chat/samshin', sym: '慈', nameKey: 'samshinSaju' },
    { to: '/astrology', sym: '天', nameKey: 'astrologyChart' },
    { to: '/saju-pro', sym: '曆', nameKey: 'manseryeok' },
  ]},
  { labelKey: 'tarotAndGunghap', items: [
    { to: '/tarot', sym: '卦', nameKey: 'tarotReading' },
    { to: '/gunghap', sym: '宮', nameKey: 'gunghapAnalysis' },
  ]},
  { labelKey: 'community', items: [
    { to: '/live', sym: '生', nameKey: 'liveLounge' },
    { to: '/celebrities', sym: '星', nameKey: 'celebritySaju' },
    { to: '/gallery', sym: '暢', nameKey: 'chokjumGallery' },
  ]},
  { labelKey: 'tools', items: [
    { to: '/board', sym: '談', nameKey: 'sajuBoard' },
    { to: '/encyclopedia', sym: '典', nameKey: 'encyclopedia' },
    { to: '/psychtest', sym: '心', nameKey: 'psychtest' },
    { to: '/franchise', sym: '屋', nameKey: 'siteFranchise' },
    { to: '/saju-farm', sym: '園', nameKey: 'sajuFarm' },
    { to: '/saju-map', sym: '圖', nameKey: 'sajuMap' },
  ]},
  { labelKey: 'myMyeongri', items: [
    { to: '/myeongri', sym: '鑑', nameKey: 'myeongriPortal' },
    { to: '/mypage', sym: '人', nameKey: 'mypage' },
    { to: '/charge', sym: '寶', nameKey: 'chargePoints' },
  ]},
];

// Both menus resolve service labels and paths from the same registry.
const SERVICE_LINKS = NAV_KEYS.flatMap(group => group.items);
const DESKTOP_LINKS = [
  { to: '/', nameKey: 'home' },
  ...['/fortune', '/saju-garden', '/character-chat/hammyo', '/character-chat/keonyang', '/character-chat/sanharabeoji', '/character-chat/samshin', '/tarot', '/gunghap',
    '/saju-pro', '/astrology', '/live', '/gallery']
    .map(path => SERVICE_LINKS.find(item => item.to === path)),
];

const DESKTOP_LABELS = {
  ko: ['홈', '전통 사주', '사주 아트', '햄묘', '커냥', '산할아버지', '삼신할머니', '타로', '궁합', '만세력', '점성술', '라이브', '커뮤니티'],
  en: ['Home', 'Traditional', 'Art Studio', 'Hammyo', 'Keonyang', 'Mountain Elder', 'Granny Samshin', 'Tarot', 'Match', 'Chart', 'Astrology', 'Live', 'Community'],
  ja: ['ホーム', '伝統四柱', '四柱アート', 'ハムミョ', 'コニャン', '山のおじいさん', 'サムシンおばあさん', 'タロット', '相性', '万世暦', '占星術', 'ライブ', '交流'],
  ru: ['Главная', 'Сажу', 'Арт-студия', 'Хаммё', 'Конян', 'Горный дедушка', 'Бабушка Самсин', 'Таро', 'Пара', 'Карта', 'Астрология', 'Live', 'Общение'],
  de: ['Start', 'Traditionell', 'Saju-Kunst', 'Hammyo', 'Keonyang', 'Bergopa', 'Oma Samshin', 'Tarot', 'Partner', 'Horoskop', 'Astrologie', 'Live', 'Community'],
};

/* ── Notification Bell ── */
const NotiBell = () => {
  const [count, setCount] = useState(0);
  const navigate = useNavigate();
  const { lang } = useI18n();
  const { tr } = useScopedUiI18n();

  useEffect(() => {
    const fetch = () => {
      api.get('/notifications/unread-count')
        .then(r => setCount(r.data.count || 0))
        .catch(() => {});
    };
    fetch();
    const interval = setInterval(fetch, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <button onClick={() => navigate(localizePath('/notifications', lang))} aria-label={tr('notifications')} style={{
      position: 'relative', padding: 8, background: 'none', border: 'none', cursor: 'pointer',
      color: 'var(--text-primary)', fontSize: 18, minWidth: 44, minHeight: 44,
    }}>
      🔔
      {count > 0 && (
        <span style={{
          position: 'absolute', top: 0, right: 0,
          minWidth: 16, height: 16, borderRadius: 8,
          background: '#ef4444', color: '#fff',
          fontSize: 10, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 4px',
        }}>{count > 99 ? '99+' : count}</span>
      )}
    </button>
  );
};

export function Header() {
  const { user, logout, isAuthenticated } = useAuth();
  const { isDark } = useTheme();
  const { t, lang, setLang, LANG_OPTIONS } = useI18n();
  const { tr } = useScopedUiI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [langMenu, setLangMenu] = useState(false);
  const userRef = useRef(null);
  const langRef = useRef(null);
  const mobileRef = useRef(null);
  const menuButtonRef = useRef(null);
  const localePath = (path) => localizePath(path, lang);
  const logicalPathname = stripLocalePrefix(location.pathname);

  useEffect(() => {
    const h = (e) => {
      if (userRef.current && !userRef.current.contains(e.target)) setUserMenu(false);
      if (langRef.current && !langRef.current.contains(e.target)) setLangMenu(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => { setMobileOpen(false); setUserMenu(false); }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const menuButton = menuButtonRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusable = () => Array.from(mobileRef.current?.querySelectorAll('a[href], button:not([disabled])') || []);
    focusable()[0]?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') { setMobileOpen(false); return; }
      if (event.key !== 'Tab') return;
      const targets = [menuButtonRef.current, ...focusable()].filter(Boolean);
      const first = targets[0], last = targets[targets.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      menuButton?.focus();
    };
  }, [mobileOpen]);

  const doLogout = () => { logout(); navigate(localePath('/')); };

  return (
    <>
      <header className="header-root" style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 'var(--z-header)',
        background: isDark ? 'rgba(8,4,20,0.7)' : 'rgba(245,240,255,0.72)',
        backdropFilter: 'blur(40px) saturate(200%) brightness(1.04)',
        WebkitBackdropFilter: 'blur(40px) saturate(200%) brightness(1.04)',
        borderBottom: '1px solid var(--glass-border)',
        boxShadow: isDark
          ? '0 4px 24px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.14)'
          : '0 4px 24px rgba(100,60,200,0.1), inset 0 1px 0 rgba(255,255,255,0.9)',
        padding: '0 16px',
      }}>
        {/* Liquid Glass shimmer overlay */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: isDark
            ? 'linear-gradient(180deg, rgba(160,100,255,0.05) 0%, transparent 60%)'
            : 'linear-gradient(180deg, rgba(255,255,255,0.25) 0%, transparent 60%)',
        }} />
        <div style={{
          position: 'absolute', left: 0, right: 0, top: 0, height: 1, pointerEvents: 'none',
          background: isDark
            ? 'linear-gradient(90deg, transparent, rgba(200,160,255,0.3), transparent)'
            : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)',
        }} />
        <div style={{ maxWidth: 1080, margin: '0 auto', display: 'flex', alignItems: 'center', height: 52, gap: 8 }}>
          {/* Logo */}
          <Link to={localePath('/')} style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, textDecoration: 'none' }}>
            <span className="saju2you-logo-glyph">命</span>
            <span className="saju2you-brand-text">
              {tr('brand')}<em style={{ fontStyle: 'normal', color: 'var(--p2-pink)' }}>·</em>命
            </span>
          </Link>

          <nav className="saju2you-desktop-nav" aria-label={tr('mainMenu')} style={{ flex: 1, minWidth: 0, overflowX: 'auto', whiteSpace: 'nowrap', scrollbarWidth: 'none' }}>
            {DESKTOP_LINKS.map((item, index) => (
              <Link key={item.to} to={localePath(item.to)} className={logicalPathname === item.to ? 'active' : ''} style={{ flexShrink: 0 }}>
                {DESKTOP_LABELS[lang]?.[index] || t[item.nameKey]}
              </Link>
            ))}
          </nav>

          {/* Right side */}
          <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', flexShrink: 0 }}>
            {isAuthenticated ? (
              <>
              <div className="header-nav-mobile-hide"><NotiBell /></div>
              <div ref={userRef} className="header-nav-mobile-hide" style={{ position: 'relative' }}>
                <button onClick={() => setUserMenu(!userMenu)} style={{
                  padding: '6px 14px', borderRadius: 'var(--radius-full)',
                  background: 'var(--glass-bg-subtle)', border: '1px solid var(--glass-border)',
                  fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <span style={{ fontFamily: 'var(--font-serif)', fontSize: 13, color: 'var(--accent-gold)' }}>人</span>
                  {user?.nickname || tr('setId')}
                </button>
                {userMenu && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 8,
                    minWidth: 180, padding: 8,
                    background: 'var(--glass-bg-strong)',
                    backdropFilter: 'var(--glass-blur-heavy)',
                    WebkitBackdropFilter: 'var(--glass-blur-heavy)',
                    border: '1px solid var(--glass-border-strong)',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: 'var(--glass-shadow-lg)',
                    zIndex: 200,
                  }}>
                    {[
                      { to: '/notifications', sym: '報', nameKey: 'notifications' },
                      { to: '/myeongri', sym: '鑑', nameKey: 'myeongriPortal' },
                      { to: '/mypage', sym: '人', nameKey: 'mypage' },
                      { to: '/charge', sym: '寶', nameKey: 'chargePoints' },
                      ...((user?.email === 'mobidicmcn@gmail.com' || user?.role === 'admin')
                        ? [{ to: '/admin-erp', sym: '管', nameKey: 'adminERP' }]
                        : []),
                    ].map((item, i) => (
                      <Link key={i} to={localePath(item.to)} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                        borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-sm)',
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--glass-bg-subtle)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                        <span style={{ fontFamily: 'var(--font-serif)', fontSize: 13, color: 'var(--accent-gold)', opacity: 0.7, width: 18, textAlign: 'center' }}>{item.sym}</span>
                        <span>{t[item.nameKey]}</span>
                      </Link>
                    ))}
                    <div style={{ height: 1, background: 'var(--separator)', margin: '6px 0' }} />
                    <button onClick={doLogout} style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                      borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-sm)', color: 'var(--accent-red)',
                    }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239,68,68,0.06)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                      {t.logout}
                    </button>
                  </div>
                )}
              </div>
              </>
            ) : (
              <div className="header-nav-mobile-hide" style={{ display: 'flex', gap: 8 }}>
                <Link to={localePath('/login')} className="glass-btn" style={{ padding: '8px 18px', fontSize: 'var(--text-sm)' }}>{t.login}</Link>
                <Link to={localePath('/register')} className="glass-btn-primary" style={{ padding: '8px 18px', fontSize: 'var(--text-sm)' }}>{t.register}</Link>
              </div>
            )}

            {/* Language selector */}
            <div ref={langRef} style={{ position: 'relative' }}>
              <button onClick={() => setLangMenu(!langMenu)} aria-label={tr('selectLanguage')} style={{
                padding: '8px 12px', borderRadius: 'var(--radius-full)',
                background: 'var(--glass-bg-subtle)', border: '1px solid var(--glass-border)',
                fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center',
                minWidth: 44, minHeight: 44,
              }}>
                {LANG_OPTIONS.find(l => l.code === lang)?.flag || '🌐'}
              </button>
              {langMenu && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 8,
                  minWidth: 140, padding: 4,
                  background: 'var(--glass-bg-strong)',
                  backdropFilter: 'var(--glass-blur-heavy)',
                  WebkitBackdropFilter: 'var(--glass-blur-heavy)',
                  border: '1px solid var(--glass-border-strong)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--glass-shadow-lg)',
                  zIndex: 100,
                }}>
                  {LANG_OPTIONS.map(opt => (
                    <button key={opt.code} onClick={() => { setLang(opt.code); setLangMenu(false); }} style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 12px', borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--text-sm)', fontWeight: lang === opt.code ? 700 : 400,
                      background: lang === opt.code ? 'var(--glass-bg-subtle)' : 'transparent',
                      border: 'none', cursor: 'pointer', color: 'var(--text-primary)',
                    }}>
                      <span style={{ fontSize: 16 }}>{opt.flag}</span> {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Hamburger (far right) */}
            <button ref={menuButtonRef} aria-expanded={mobileOpen} aria-controls="mobile-navigation" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? tr('closeMenu') : tr('openMenu')} style={{ padding: 8, minWidth: 44, minHeight: 44 }}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                {mobileOpen
                  ? <path d="M6 6L16 16M16 6L6 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  : <path d="M3 6H19M3 11H19M3 16H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>}
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile slide-out */}
      {mobileOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99, background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(6px)',
        }} onClick={() => setMobileOpen(false)}>
          <nav id="mobile-navigation" ref={mobileRef} className="mobile-navigation" aria-label={tr('openMenu')} onClick={(e) => e.stopPropagation()} style={{
            position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(90vw, 340px)',
            background: 'var(--glass-bg-strong)',
            backdropFilter: 'var(--glass-blur-heavy)',
            WebkitBackdropFilter: 'var(--glass-blur-heavy)',
            borderLeft: '1px solid var(--glass-border)',
            padding: '72px 16px max(24px, env(safe-area-inset-bottom))', overflowY: 'auto', overscrollBehavior: 'contain',
            animation: 'slideInRight 0.25s ease',
          }}>
            {!isAuthenticated && (
              <div style={{ marginBottom: 12, display: 'flex', gap: 8, padding: '0 16px' }}>
                <Link to={localePath('/login')} className="glass-btn" style={{ flex: 1, justifyContent: 'center' }}>{t.login}</Link>
                <Link to={localePath('/register')} className="glass-btn-primary" style={{ flex: 1, justifyContent: 'center' }}>{t.register}</Link>
              </div>
            )}
            {isAuthenticated && (
              <div className="mobile-account-links">
                <Link className="glass-btn" to={localePath('/mypage')}>{t.mypage}</Link>
                <Link className="glass-btn-primary" to={localePath('/charge')}>{t.chargePoints}</Link>
              </div>
            )}
            <Link to={localePath('/')} style={{ display: 'block', padding: '12px 16px', fontWeight: 700, fontSize: 'var(--text-base)', borderRadius: 'var(--radius-sm)' }}>
              {t.home}
            </Link>
            {NAV_KEYS.map((g, i) => (
              <div key={i} style={{ marginTop: 12 }}>
                <div style={{ padding: '8px 16px', fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {t[g.labelKey]}
                </div>
                {g.items.map((item, j) => (
                  <Link key={j} to={localePath(item.to)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                    <span style={{ fontFamily: 'var(--font-serif)', fontSize: 13, color: 'var(--accent-gold)', opacity: 0.6, width: 18, textAlign: 'center' }}>{item.sym}</span> {t[item.nameKey]}
                  </Link>
                ))}
              </div>
            ))}
            {isAuthenticated && (
              <div style={{ marginTop: 12 }}>
                <div style={{ padding: '8px 16px', fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {user?.nickname || tr('setId')}
                </div>
                <Link to={localePath('/notifications')} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                  <span style={{ fontFamily: 'var(--font-serif)', fontSize: 13, color: 'var(--accent-gold)', opacity: 0.6, width: 18, textAlign: 'center' }}>報</span> {t.notifications}
                </Link>
                <button onClick={doLogout} style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                  borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--accent-red)',
                  background: 'transparent', border: 'none', textAlign: 'left',
                }}>
                  <span style={{ fontFamily: 'var(--font-serif)', fontSize: 13, opacity: 0.6, width: 18, textAlign: 'center' }}>出</span> {t.logout}
                </button>
              </div>
            )}



          </nav>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-only { display: none !important; }
        }
      `}</style>
    </>
  );
}
